import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { useSearchOrderFulfillmentsLazyQuery } from '../../../graphql/queries/searchOrderFulfillments.generated';
import { OrderFulfillmentReducedFragment } from '../../../graphql/fragments/orderFulfillmentReduced.generated';
import { LayoutStoreInstance } from '../../../LayoutStore';
import i18n from '@/utils/i18n';
import { ORDER_FULFILLMENT_PAGE_SIZE } from '../components/OrderFulfillmentsTableWithFilter';
import OrderFulfillmentsTable, {
  OrderFulfillmentsTableHeaderSort,
} from '../components/OrderFulfillmentsTable';
import { MainHeader } from '../../../components/Layout/MainHeader';
import { ContentSection } from '../../../components/Layout/ContentSection';
import {
  OrderFulfillmentStatus,
  OrderFulfillmentType,
} from '../../../graphql/generatedTypes';
import CustomMultiSelect, {
  MultiSelectOption,
} from '../../../components/CustomMultiSelect/CustomMultiSelect';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const OfTableHeaderSort: OrderFulfillmentsTableHeaderSort[] = [
  'event',
  'eventAccount',
  'itemLocation',
  'signer',
  'type',
  'status',
  'itemCount',
  'timestamp',
  'detail',
];

const OrderFulfillmentSearchPage = () => {
  useDocumentTitle('PageTitles:orderFulfillmentSearch');

  // Order fulfillment type options for the multi-select (moved inside component to ensure translations are loaded)
  const orderFulfillmentTypeOptions: MultiSelectOption[] = Object.values(
    OrderFulfillmentType,
  ).map((type) => ({
    value: type,
    label: i18n.t(`OrderFulfillmentType:${type?.toUpperCase()}`),
  }));

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [selectedOFStatus, setSelectedOFStatus] = useState<string | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<(string | number)[]>([]);
  const [search, { loading, error }] = useSearchOrderFulfillmentsLazyQuery();
  const [orderFulfillments, setOrderFulfillments] = useState<
    OrderFulfillmentReducedFragment[]
  >([]);

  const searchOrderFulfillments = async () => {
    let skip = (page - 1) * ORDER_FULFILLMENT_PAGE_SIZE;
    skip = skip < 0 ? 0 : skip;

    // Convert selected types to the correct format for the backend
    const typesFilter =
      selectedTypes.length > 0
        ? selectedTypes.map((type) => type as OrderFulfillmentType)
        : undefined;

    await search({
      variables: {
        start_date: startDate,
        end_date: endDate,
        query: query,
        status: selectedOFStatus as OrderFulfillmentStatus,
        types: typesFilter,
        skip,
        take: ORDER_FULFILLMENT_PAGE_SIZE,
      },
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        setOrderFulfillments(data.searchOrderFulfillments.order_fulfillments);
        setTotal(data.searchOrderFulfillments.total);
      },
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await searchOrderFulfillments();
  };

  if (error) {
    console.log(error);
    LayoutStoreInstance.setError(
      i18n.t('OrderFulfillment:searchError') + error.message,
    );
  }

  useEffect(() => {
    (async () => {
      await searchOrderFulfillments();
    })();
  }, []);

  useEffect(() => {
    (async () => {
      await searchOrderFulfillments();
    })();
  }, [page]);

  const pageCount = Math.ceil(total / ORDER_FULFILLMENT_PAGE_SIZE);

  return (
    <div>
      <MainHeader title={i18n.t('OrderFulfillment:SearchOrderfulfillments')} />

      <ContentSection
        title={i18n.t('OrderFulfillment:Search')}
        collapsable={false}
      >
        <Form onSubmit={(e) => handleSubmit(e)}>
          <Row form>
            <Col md={6}>
              {/* Column for 'startDate' */}
              <FormGroup className="my-2">
                <Label for="startDate" className="form-label">
                  {i18n.t('Common:startDate')}
                </Label>
                <Input
                  type="date"
                  name="startDate"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="form-control"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              {/* Column for 'endDate' */}
              <FormGroup className="my-2">
                <Label for="endDate" className="form-label">
                  {i18n.t('Common:endDate')}
                </Label>
                <Input
                  type="date"
                  name="endDate"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="form-control"
                />
              </FormGroup>
            </Col>
          </Row>

          <Row form>
            <FormGroup className="my-2">
              <Label for="query" className="form-label">
                {i18n.t('OrderFulfillment:query')}
              </Label>
              <Input
                type="text"
                name="query"
                id="query"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={i18n.t('OrderFulfillment:queryPlaceholder')}
                className="form-control"
              />
            </FormGroup>
          </Row>

          <Row form>
            <Col md={6}>
              <FormGroup className="my-2">
                <Label className="form-label">
                  {i18n.t('OrderFulfillment:types')}
                </Label>
                <CustomMultiSelect
                  options={orderFulfillmentTypeOptions}
                  value={selectedTypes}
                  onChange={setSelectedTypes}
                  placeholder={i18n.t('OrderFulfillment:selectTypes')}
                  searchable={false}
                  clearable={true}
                  maxSelectedLabels={2}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="my-2">
                <Label className="form-label">
                  {i18n.t('OrderFulfillment:orderfulfillmentStatus')}
                </Label>
                <Dropdown
                  isOpen={showStatusDropdown}
                  toggle={() => {
                    setShowStatusDropdown(!showStatusDropdown);
                  }}
                >
                  <DropdownToggle caret>
                    {selectedOFStatus
                      ? i18n.t(`OrderFulfillment:status_${selectedOFStatus}`)
                      : i18n.t('OrderFulfillment:orderfulfillmentStatus')}
                  </DropdownToggle>

                  <DropdownMenu>
                    <DropdownItem
                      onClick={() => {
                        setSelectedOFStatus('');
                      }}
                    >
                      {i18n.t(`Common:all`)}
                    </DropdownItem>
                    {Object.values(OrderFulfillmentStatus).map((value) => {
                      return (
                        <DropdownItem
                          key={value}
                          onClick={() => {
                            setSelectedOFStatus(value);
                          }}
                        >
                          {i18n.t(`OrderFulfillment:status_${value}`)}
                        </DropdownItem>
                      );
                    })}
                  </DropdownMenu>
                </Dropdown>
              </FormGroup>
            </Col>
          </Row>

          <div className="d-flex justify-content-end">
            <Button type="submit" color="primary" disabled={loading}>
              {i18n.t('Common:search')}
            </Button>
          </div>
        </Form>

        <h3 className="mb-3 mt-3">{i18n.t('Common:searchResults')}</h3>
        <OrderFulfillmentsTable
          orderFulfillments={orderFulfillments}
          loading={loading}
          error={error}
          page={page}
          pageCount={pageCount}
          setPage={setPage}
          headers={OfTableHeaderSort}
        />
      </ContentSection>
    </div>
  );
};

export default OrderFulfillmentSearchPage;
